/**
 * Training — member education center.
 *
 * Two tabs: Lessons (the 7-module operator guide, for readers) and Videos
 * (coming soon, for watchers). Lesson content mirrors
 * docs/member-guide/operator-guide.md — keep the two in sync when the
 * workflow changes.
 */
import { useState } from "react";
import { BRIAN_PROMPT_GROUPS } from "../lib/brian-prompts";
import {
  BookOpenText, GraduationCap, PlayCircle, ChevronDown, ChevronUp,
  ShieldAlert, Clapperboard, MessageSquareText, Copy, Check,
} from "lucide-react";
import { Shell } from "../components/Shell";

type Module = {
  n: number;
  title: string;
  time: string;
  body: React.ReactNode;
};

function Rule({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="banner-warning banner-rail" style={{ display: "flex", gap: 10, alignItems: "flex-start", margin: "12px 0" }}>
      <ShieldAlert size={16} aria-hidden="true" style={{ marginTop: 2, flexShrink: 0 }} />
      <span><strong>{title}.</strong> {children}</span>
    </div>
  );
}

const MODULES: Module[] = [
  {
    n: 1, title: "Set Up Your Company", time: "once · ~20 min",
    body: (
      <>
        <p>Everything lives in <strong>Company Settings</strong> (left sidebar).</p>
        <ol>
          <li><strong>Client-facing branding</strong> — business name, logo (PNG/JPG/WebP under 1 MB), sender emails, phone, address. Clients see <em>your</em> brand everywhere.</li>
          <li><strong>Internal notifications</strong> — where your own alerts land.</li>
          <li><strong>Credit monitoring — client enrollment</strong> — paste your MyFreeScoreNow (and SmartCredit) affiliate links so clients enroll under you and you can pull their 3-bureau report.</li>
          <li><strong>Mailing accounts — your own carriers</strong> — dispute letters mail on <strong>your</strong> Lob and/or LetterStream keys. Lob issues a key instantly (add a payment card there too); LetterStream keys come from their support team and your balance must be funded. <strong>No keys saved = no mail goes out. There is no house carrier.</strong></li>
          <li><strong>Manage staff / admin</strong> (owner) — up to 5 logins: Manager / Analyst / Viewer.</li>
        </ol>
      </>
    ),
  },
  {
    n: 2, title: "Bring In a Client", time: "per client · ~5 min + signature",
    body: (
      <>
        <ol>
          <li><strong>Clients → Add client</strong> (or <strong>Import CSV</strong>).</li>
          <li><strong>Send the intake</strong> — branded link: personal info, ID uploads (government ID, Social Security card, proof of address — these ride with every letter bundle), monitoring enrollment.</li>
          <li><strong>Service agreement</strong> — sent from the client's profile, signed in their portal.</li>
        </ol>
        <Rule title="Legal gate — not a formality">
          CROA requires a signed contract before services, so the software refuses to queue
          mail for an unsigned client. A Send button saying “agreement not signed” is this
          gate doing its job.
        </Rule>
      </>
    ),
  },
  {
    n: 3, title: "Get the Report & Run the Analysis", time: "~5 min",
    body: (
      <>
        <ol>
          <li><strong>Credit Reports → Import</strong> — credentialed import (the client's own MyFreeScoreNow / SmartCredit login, stored encrypted with consent, pulls all three bureaus in about a minute) or PDF upload.</li>
          <li>Open the client → <strong>Analysis tab</strong> → <strong>Analyze Report</strong>.</li>
          <li>Findings are ranked <strong>Critical / High / Medium / Low</strong> — collections, charge-offs, late-pay patterns, items past the 7-year clock, re-aged dates, cross-bureau personal-info mismatches, inquiries with no matching account. Each carries its plain-English why and legal basis.</li>
        </ol>
        <p className="muted"><strong>Trust the ranking.</strong> Disputing technically-accurate items burns a round, comes back “verified,” and erodes trust. Dispute what can actually move.</p>
      </>
    ),
  },
  {
    n: 4, title: "Build the Round & the Letters", time: "~10 min",
    body: (
      <>
        <ol>
          <li>On the <strong>Analysis tab</strong>: <strong>Run Full Dispute Flow</strong> — auto-selects Critical + High findings and drafts the whole round. Or hand-pick findings.</li>
          <li>Items route automatically: <strong>§611 reinvestigation</strong> (the workhorse — personal info, inquiries, inaccuracies), <strong>§605B</strong> (collections & charge-offs), <strong>Late payment</strong>, <strong>Public records</strong> (furnisher-identity + Rule 9037 language built in), <strong>§623 furnisher</strong> (goes to the creditor — never bundled with bureau mail), <strong>LexisNexis</strong> (standalone, never bundled).</li>
          <li>Review on <strong>Letters</strong> — preview, edit, approve. Clean black-and-white PDFs, no logos (a branded letter screams “credit repair company” to e-OSCAR).</li>
          <li>Each bureau gets a <strong>bundle</strong> — its letters + the client's ID docs, named like <code>equifax-Smith-r1-2026-08-13.pdf</code>. Download from the round on <strong>Disputes</strong> or from <strong>Letters</strong>.</li>
        </ol>
      </>
    ),
  },
  {
    n: 5, title: "Mail It & Work the Clock", time: "~5 min, then patience",
    body: (
      <>
        <ol>
          <li><strong>Letter Mailing</strong> — the <strong>Mail provider status</strong> panel shows a Configured / Not configured pill per carrier. Not configured → Module 1, step 4.</li>
          <li>Queue from Letters or <strong>New mail job</strong> (single letter or <strong>Bureau bundle</strong>). <strong>You pick the carrier every time</strong> — Lob, LetterStream, or Manual. <strong>Certified</strong> is the usual class for dispute rounds.</li>
          <li><strong>Send</strong>, or check several and <strong>Send all</strong>. Ladder: queued → sent → in transit → delivered (+ staff mailed / returned / failed / cancelled). <strong>Poll</strong> refreshes, <strong>Track</strong> opens tracking, Manual jobs get <strong>Mark delivered</strong>.</li>
          <li><strong>The clock:</strong> bureaus get <strong>30 days</strong> under §611. Upload responses on the client's <strong>Correspondence tab</strong>.</li>
          <li><strong>“Verified”?</strong> Escalate — Method of Verification demand (§611(a)(7)), §623 direct dispute, or a new round. CFPB (Module 7) can be staged any time — the same day as the letters if you choose.</li>
        </ol>
      </>
    ),
  },
  {
    n: 6, title: "Attorney Brian — Your AI Counsel", time: "needs your own Claude",
    body: (
      <>
        <p>Attorney Brian runs inside <strong>your own Claude</strong> and works your company's data in plain English — drafts rounds, reads analyses, queues mail on your carrier accounts, preps federal filings.</p>
        <Rule title="What you need first">
          Your own Claude subscription (Claude Pro or Max) with Claude Code installed. Not
          included in the software price. No Claude = no Brian.
        </Rule>
        <ol>
          <li><strong>Company Settings → Attorney Brian — connect your Claude</strong> (owner login) → name the connection → <strong>Connect Attorney Brian</strong>.</li>
          <li>Copy the one-line command (shown once) → paste into your terminal → Enter.</li>
          <li>Open Claude Code: <em>“Use Attorney Brian and list my clients.”</em> If he answers with your clients, you're live.</li>
        </ol>
        <p><strong>Talk to him like this:</strong> “Analyze Jordan's latest report and tell me what's worth disputing.” · “Draft round one from the critical and high findings.” · “Which clients are past the 30-day window?” · “Queue Jordan's round for certified mail on Lob.” · “Prepare the CFPB complaint and give me the review link.”</p>
        <p><strong>He refuses, by design:</strong> pressing Submit on a CFPB complaint (a human does), attesting identity theft for a client (the client types their own — fabricated affidavits are federal crimes), promising score increases (CROA). He only sees your company's data — every request is checked server-side.</p>
      </>
    ),
  },
  {
    n: 7, title: "Federal Escalation — FTC & CFPB", time: "read this one twice",
    body: (
      <>
        <Rule title="The four rules">
          <strong>1 · Order:</strong> identity-theft cases file FTC first, CFPB second.{" "}
          <strong>2 · Timing:</strong> CFPB can be staged the same day the bureau letters go out. Brian warns when the bureaus have had under 45 days, but it never blocks; a person still presses Submit on the CFPB site.{" "}
          <strong>3 · Attestation:</strong> the client's own words, in writing — never typed for them.{" "}
          <strong>4 · Submit:</strong> on the CFPB portal a human presses Submit. Every time.
        </Rule>
        <p><strong>7A — Prepare here:</strong> <strong>Online Dispute → Prepare a filing</strong> → client + kind (<strong>FTC report</strong> / <strong>CFPB complaint</strong>) → <strong>Prepare draft</strong> → edit + <strong>Save narrative</strong> → download each bureau's <strong>Bundle</strong> → for identity theft, <strong>Generate client review link</strong> (single-use, 14 days) and send it to the client.</p>
        <p><strong>7B — FTC (IdentityTheft.gov):</strong> this is the identity-theft report that powers the §605B block letters (a 4-business-day bureau block). It's a step-by-step interview on a free government site. <strong>Never done one? Module 8 below is the complete click-by-click walkthrough</strong> — read it before your first filing.</p>
        <p><strong>7C — CFPB (consumerfinance.gov → Submit a complaint), the 5-step wizard:</strong></p>
        <ol>
          <li><strong>Product:</strong> “Credit reporting or other personal consumer reports” → <strong>Credit reporting</strong>.</li>
          <li><strong>Issue:</strong> <strong>Improper use of your report</strong> → <strong>Reporting company used your report improperly</strong>. Then Yes / Yes / <strong>No</strong> (“No” = they never adequately responded — the true story).</li>
          <li><strong>What happened:</strong> paste the prepared narrative into <strong>both</strong> boxes — identical, word for word. Attach the bureau bundle PDF(s). Leave “publish my complaint” unchecked.</li>
          <li><strong>Company:</strong> type slowly, pick the all-caps entity — <strong>EQUIFAX, INC.</strong> · <strong>EXPERIAN INFORMATION SOLUTIONS INC.</strong> · <strong>TRANSUNION INTERMEDIATE HOLDINGS, INC.</strong> Check all three ID boxes (SSN last-4, DOB, name as it appears), eye-verify the SSN. One complaint covers all three bureaus — answer “another company?” <strong>Yes</strong> and add each with its own bundle.</li>
          <li><strong>People:</strong> “for myself” → <strong>Review</strong> → read everything → <strong>the human presses Submit</strong>.</li>
        </ol>
        <p>Afterward: <strong>Mark filed &amp; log</strong> with the confirmation number — your audit trail. CFPB responses usually land in 15–60 days; log the outcome.</p>
      </>
    ),
  },
  {
    n: 8, title: "FTC Identity Theft Report — the complete walkthrough", time: "first-timer · read fully before filing",
    body: (
      <>
        <Rule title="Only for real identity theft">
          File this ONLY when your client attests, in their own words, that the accounts are
          fraudulent — they did not open them. A knowingly false FTC report is a federal crime
          (18 U.S.C. §1001). This is not a general dispute tactic. If the accounts are simply
          inaccurate (not fraud), use the regular §611 dispute round instead — not this.
        </Rule>

        <p><strong>What you'll need in front of you before you start (5 minutes of prep):</strong></p>
        <ol>
          <li><strong>The client's fraudulent accounts.</strong> This is the "where do I get the accounts?" answer: they come from the client's own credit report. Open the client → <strong>Analysis</strong> tab (or their credit report). The accounts to enter are the <strong>collections and tradelines the client says are NOT theirs</strong> — the ones they flagged as fraud at intake. For each one you'll want: the <strong>company/creditor name</strong>, the <strong>approximate date it was opened</strong>, and the <strong>dollar amount / balance</strong>. All of that is on the analysis. If you prepared the filing in <strong>Online Dispute → Prepare a filing (FTC report)</strong>, Attorney Brian already pulled these into the draft — open it and keep it beside you.</li>
          <li><strong>The client on the phone or next to you.</strong> The FTC texts a one-time code to the client's phone mid-way through. You cannot finish without it, so have the client reachable.</li>
          <li><strong>The client's basics:</strong> full legal name, date of birth, current address, email, and the phone number that will receive the text code.</li>
        </ol>

        <p><strong>Do you need to create an account on the FTC site? No.</strong> IdentityTheft.gov lets you <strong>submit without creating an account</strong> — that's the simplest path and what these steps use. (You <em>can</em> optionally make a free account to save and return later, but it's not required, and there is never a fee. The site is the real Federal Trade Commission — the only correct address is <strong>identitytheft.gov</strong>. Don't use any site that asks for payment.)</p>

        <p><strong>Step by step — exactly what you'll click:</strong></p>
        <ol>
          <li>Go to <strong>identitytheft.gov</strong> → click <strong>Report Identity Theft</strong> (also shown as "Tell us what happened"). This opens the interview.</li>
          <li><strong>"Did someone use your information to open accounts, or to buy something?"</strong> → choose <strong>Yes</strong> (someone used my info to open an account, etc.).</li>
          <li><strong>"What did the identity thief use your information for?"</strong> → check <strong>Credit card accounts</strong> (check any other type that also applies) → <strong>Continue</strong>.</li>
          <li><strong>"Tell us how your information was misused."</strong> → check <strong>"to open a fraudulent credit card account"</strong> → <strong>Continue</strong>.</li>
          <li>You'll hit a page titled <strong>"Report Identity Theft to the FTC"</strong> with a notice that filing a false report is illegal. This is normal — your client's attestation is what makes it true. Click <strong>Continue</strong>.</li>
          <li><strong>Add the fraudulent accounts — one at a time.</strong> This is the core of the report. For <em>each</em> negative account the client says isn't theirs (the collections and charge-offs flagged on the Analysis tab):
            <ul>
              <li><strong>Company name</strong> — the creditor/collector exactly as it reads on the report (e.g. "MIDLAND CREDIT MANAGEMENT"). If it's a collector on an original debt, use the name shown on the tradeline.</li>
              <li><strong>Date opened</strong> — the account's open date from the report; an approximate month/year is fine if the exact day isn't shown.</li>
              <li><strong>Amount</strong> — the balance or original amount from the report.</li>
              <li>Then click <strong>"Add another"</strong> and enter the next fraudulent account. Repeat until every account the client is disputing as fraud is listed. When they're all in → <strong>Continue</strong>.</li>
            </ul>
          </li>
          <li><strong>Fraudulent inquiries.</strong> If unauthorized <strong>hard inquiries</strong> appear on the report (pulls the client never authorized — you'll see them on the Analysis tab under Inquiries), note them here too. The interview centers on accounts, so if there's no dedicated inquiry field, list each unauthorized inquiry — <strong>creditor name and date</strong> — inside the <strong>personal statement</strong> in the next step. They get blocked/removed on the bureau side via the §605B block that this report powers.</li>
          <li><strong>Your (the client's) information.</strong> Enter first &amp; last name, then the <strong>mobile phone number</strong> that will get the code. Click <strong>Get My Code</strong>. <strong>The FTC now texts a 6-digit code to the client's phone.</strong> The client reads it to you; type it in and click <strong>Verify</strong>. Then finish the rest: email, date of birth, and current address.</li>
          <li><strong>"Do you know who did this?"</strong> → almost always <strong>"I don't know who the identity thief is."</strong> → Continue.</li>
          <li><strong>Additional details</strong> → nothing required → <strong>Continue</strong> (or Skip).</li>
          <li><strong>Personal statement — the client's own words.</strong> This is the attestation. If you generated the review link, paste the exact narrative the client already signed. If you're writing it live with the client, it should say, in their voice: <em>that they did not open or authorize these accounts, roughly when they discovered them, that they've never done business with these creditors, and that they want them removed.</em> List the fraudulent <strong>accounts</strong> by name and any unauthorized <strong>inquiries</strong> (creditor + date) right in the statement so everything is on the record. Keep it factual and first-person → Continue.</li>
          <li><strong>Review &amp; sign.</strong> Read the summary, check the <strong>"under penalty of perjury"</strong> box, type the client's full legal name as the signature, and click <strong>Finalize</strong>.</li>
          <li>The site offers to create an account — choose <strong>"No thanks, submit without an account,"</strong> confirm on the pop-up, and continue. It generates the report.</li>
        </ol>

        <Rule title="Save these two things the instant the report generates">
          (1) the <strong>FTC report number</strong>, and (2) the <strong>Identity Theft Report PDF</strong> — click
          <strong> download</strong> right away. IdentityTheft.gov does not always let you retrieve them later,
          so capture both before you leave the page.
        </Rule>

        <p><strong>Back here in the software:</strong> open the client → <strong>Online Dispute</strong> → the FTC filing → <strong>Mark filed &amp; log</strong> and enter the report number. Then upload the report PDF to the client's <strong>Correspondence</strong> tab so it rides with the §605B block letters. That report number is what triggers the bureaus' 4-business-day block under §605B.</p>

        <p className="muted small"><strong>If the client can't get the text code:</strong> confirm the phone number is a mobile that can receive SMS, and try "Get My Code" again. Nothing is lost — the report isn't filed until the final Finalize step. <strong>If the site looks different from these steps,</strong> the FTC occasionally rearranges the interview; the order of information it asks for is the same, so follow the on-screen questions and enter the same data.</p>
      </>
    ),
  },
];

const PLANNED_VIDEOS: Array<{ title: string; length: string }> = [
  { title: "The big picture — staff app, client portal, the loop", length: "≈2 min" },
  { title: "Company setup: branding, staff & your mailing accounts", length: "≈2 min" },
  { title: "Intake to signed agreement", length: "≈2 min" },
  { title: "Importing reports & reading the analysis", length: "≈2 min" },
  { title: "Run Full Dispute Flow: rounds, letters & bundles", length: "≈3 min" },
  { title: "Letter Mailing on your own carriers", length: "≈2 min" },
  { title: "Attorney Brian: connect your Claude", length: "≈3 min" },
  { title: "Filing with the FTC & CFPB, step by step", length: "≈4 min" },
];

export default function TrainingPage() {
  const [tab, setTab] = useState<"lessons" | "prompts" | "videos">("lessons");
  const [open, setOpen] = useState<number | null>(1);
  const [copied, setCopied] = useState<string | null>(null);
  const [group, setGroup] = useState<string>(BRIAN_PROMPT_GROUPS[0].id);

  async function copyPrompt(key: string, text: string) {
    try { await navigator.clipboard.writeText(text); } catch { /* clipboard blocked — the text is still selectable */ }
    setCopied(key); window.setTimeout(() => setCopied((c) => (c === key ? null : c)), 1600);
  }

  return (
    <Shell title="Training" subtitle="Learn to operate the system, start to finish">
      <div className="channel-picker" role="tablist" aria-label="Training format" style={{ marginBottom: 16 }}>
        <button type="button" role="tab" aria-selected={tab === "lessons"} className={tab === "lessons" ? "active" : ""} onClick={() => setTab("lessons")}>
          <BookOpenText size={14} aria-hidden="true" /> Lessons
        </button>
        <button type="button" role="tab" aria-selected={tab === "prompts"} className={tab === "prompts" ? "active" : ""} onClick={() => setTab("prompts")}>
          <MessageSquareText size={14} aria-hidden="true" /> Brian prompts
        </button>
        <button type="button" role="tab" aria-selected={tab === "videos"} className={tab === "videos" ? "active" : ""} onClick={() => setTab("videos")}>
          <PlayCircle size={14} aria-hidden="true" /> Videos <span className="muted small" style={{ marginLeft: 4 }}>coming soon</span>
        </button>
      </div>

      {tab === "lessons" && (
        <>
          <p className="muted" style={{ marginTop: 0 }}>
            Eight modules — the full operating sequence. Modules 6–8 are the deep ones on
            purpose: the software is easy; your AI counsel and the federal filings are where
            the real training is. <strong>New to filing the FTC report? Go straight to Module 8</strong> —
            it's the complete first-timer walkthrough.
          </p>
          {MODULES.map((m) => {
            const isOpen = open === m.n;
            return (
              <div className="panel" key={m.n} style={{ marginBottom: 10 }}>
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? null : m.n)}
                  aria-expanded={isOpen}
                  style={{
                    display: "flex", alignItems: "center", gap: 14, width: "100%",
                    background: "none", border: "none", cursor: "pointer",
                    padding: "14px 16px", textAlign: "left", color: "inherit",
                  }}
                >
                  <span style={{ fontFamily: "var(--font-display, serif)", fontSize: 26, color: "var(--ne-red)", minWidth: 28, fontWeight: 600 }}>{m.n}</span>
                  <span style={{ flex: 1 }}>
                    <strong style={{ fontSize: 16 }}>{m.title}</strong>
                    <span className="muted small" style={{ display: "block" }}>{m.time}</span>
                  </span>
                  {isOpen ? <ChevronUp size={17} aria-hidden="true" /> : <ChevronDown size={17} aria-hidden="true" />}
                </button>
                {isOpen && (
                  <div style={{ padding: "0 18px 16px 58px", lineHeight: 1.65 }}>
                    {m.body}
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}

      {tab === "prompts" && (
        <>
          <p className="muted" style={{ marginTop: 0 }}>
            Copy a prompt, paste it into the Claude chat that has your <strong>Attorney Brian</strong> connector
            (Company Settings → Attorney Brian), and replace the <code>[brackets]</code>. Brian only sees your
            company's clients, drafts for your review, and never mails or files on his own.
          </p>
          <div className="channel-picker" role="tablist" aria-label="Prompt groups" style={{ marginBottom: 14, flexWrap: "wrap" }}>
            {BRIAN_PROMPT_GROUPS.map((g) => (
              <button key={g.id} type="button" role="tab" aria-selected={group === g.id} className={group === g.id ? "active" : ""} onClick={() => setGroup(g.id)}>
                {g.title}
              </button>
            ))}
          </div>
          {BRIAN_PROMPT_GROUPS.filter((g) => g.id === group).map((g) => (
            <div key={g.id}>
              <p className="muted small" style={{ marginTop: 0 }}>{g.blurb}</p>
              {g.prompts.map((p, i) => {
                const key = `${g.id}:${i}`;
                return (
                  <div className="panel" key={key} style={{ marginBottom: 10, padding: "14px 16px" }}>
                    <div style={{ display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
                      <div style={{ flex: "1 1 280px", minWidth: 0 }}>
                        <strong style={{ fontSize: 15 }}>{p.title}</strong>
                        <div className="muted small">{p.when}</div>
                      </div>
                      <button type="button" className="ghost-button" onClick={() => copyPrompt(key, p.prompt)} aria-label={`Copy prompt: ${p.title}`}>
                        {copied === key ? <><Check size={13} aria-hidden="true" /> Copied</> : <><Copy size={13} aria-hidden="true" /> Copy</>}
                      </button>
                    </div>
                    <pre className="prompt-card__text">{p.prompt}</pre>
                    {p.tip && <p className="muted small" style={{ margin: "8px 0 0" }}><ShieldAlert size={12} aria-hidden="true" style={{ verticalAlign: "-2px" }} /> {p.tip}</p>}
                  </div>
                );
              })}
            </div>
          ))}
        </>
      )}

      {tab === "videos" && (
        <div className="panel">
          <div className="panel-header">
            <h2 style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <Clapperboard size={17} aria-hidden="true" /> Video walkthroughs — coming soon
            </h2>
          </div>
          <p className="muted" style={{ marginTop: 0 }}>
            Short screen-recorded walkthroughs of every lesson, for people who'd rather watch
            than read. They'll appear right here — same order as the lessons.
          </p>
          <div style={{ display: "grid", gap: 8 }}>
            {PLANNED_VIDEOS.map((v, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", border: "1px solid var(--ne-silver-1)", opacity: 0.75 }}>
                <PlayCircle size={18} aria-hidden="true" style={{ color: "var(--ne-red)", flexShrink: 0 }} />
                <span style={{ flex: 1 }}>{v.title}</span>
                <span className="muted small">{v.length}</span>
                <span className="muted small" style={{ border: "1px solid var(--ne-silver-1)", padding: "2px 8px", letterSpacing: ".06em", textTransform: "uppercase", fontSize: 11 }}>Soon</span>
              </div>
            ))}
          </div>
          <p className="muted small" style={{ marginBottom: 0 }}>
            <GraduationCap size={13} aria-hidden="true" style={{ verticalAlign: "-2px" }} />{" "}
            Until then, the Lessons tab covers everything the videos will.
          </p>
        </div>
      )}
    </Shell>
  );
}
